/**
 * Module Usage Tracking Hook
 *
 * Tracks which modules are being accessed and automatically updates
 * the Module.inUse field in the database.
 *
 * Features:
 * - Auto-tracks module usage based on route
 * - Manual tracking via markModuleAsUsed()
 * - Debounced updates to prevent excessive writes
 * - Local storage cache to avoid redundant DB writes
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ModuleService } from '../services/settingsService';

const USAGE_CACHE_KEY = 'module_usage_tracking';
const DEBOUNCE_DELAY = 2000; // 2 seconds

interface UsageCache {
  [route: string]: {
    lastTracked: number;
    moduleId?: string;
  };
}

// Get usage cache from localStorage
const getUsageCache = (): UsageCache => {
  try {
    const cached = localStorage.getItem(USAGE_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error('Failed to read usage cache:', error);
    return {};
  }
};

// Save usage cache to localStorage
const saveUsageCache = (cache: UsageCache) => {
  try {
    localStorage.setItem(USAGE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Failed to save usage cache:', error);
  }
};

// Check if route was recently tracked (within last 24 hours)
const wasRecentlyTracked = (route: string): boolean => {
  const cache = getUsageCache();
  const entry = cache[route];
  if (!entry) return false;

  const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
  return entry.lastTracked > oneDayAgo;
};

// Mark route as tracked
const markRouteTracked = (route: string, moduleId?: string) => {
  const cache = getUsageCache();
  cache[route] = {
    lastTracked: Date.now(),
    moduleId
  };
  saveUsageCache(cache);
};

/**
 * Hook to track module usage by route
 *
 * @param enabled - Whether to enable automatic tracking (default: true)
 */
export const useModuleUsageTracking = (enabled: boolean = true) => {
  const location = useLocation();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const trackedRoutesRef = useRef<Set<string>>(new Set());

  /**
   * Mark a specific module as used by ID
   */
  const markModuleAsUsed = useCallback(async (moduleId: string) => {
    try {
      // Fetch the module first
      const modules = await ModuleService.getAll();
      const module = modules.find(m => m.id === moduleId);

      if (!module) {
        console.warn(`Module not found: ${moduleId}`);
        return;
      }

      // Only update if not already marked as inUse
      if (!module.inUse) {
        await ModuleService.update(moduleId, {
          ...module,
          inUse: true
        });
        console.log(`Module marked as used: ${module.name}`);
      }

      // Mark in cache
      if (module.route) {
        markRouteTracked(module.route, moduleId);
      }
    } catch (error) {
      console.error('Failed to mark module as used:', error);
    }
  }, []);

  /**
   * Mark a module as used by route
   */
  const markModuleAsUsedByRoute = useCallback(async (route: string) => {
    try {
      // Check if already tracked recently
      if (wasRecentlyTracked(route)) {
        return;
      }

      // Fetch all modules and find matching route
      const modules = await ModuleService.getAll();
      const module = modules.find(m => m.route === route || route.startsWith(m.route || ''));

      if (!module) {
        // No module found for this route, skip tracking
        return;
      }

      // Only update if not already marked as inUse
      if (!module.inUse && module.id) {
        await ModuleService.update(module.id, {
          ...module,
          inUse: true
        });
        console.log(`Module auto-tracked: ${module.name} (${route})`);
      }

      // Mark in cache
      markRouteTracked(route, module.id);
    } catch (error) {
      console.error('Failed to track module usage:', error);
    }
  }, []);

  /**
   * Track current route (debounced)
   */
  const trackCurrentRoute = useCallback(() => {
    const currentRoute = location.pathname;

    // Skip already tracked routes in this session
    if (trackedRoutesRef.current.has(currentRoute)) {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced timer
    debounceTimerRef.current = setTimeout(() => {
      markModuleAsUsedByRoute(currentRoute);
      trackedRoutesRef.current.add(currentRoute);
    }, DEBOUNCE_DELAY);
  }, [location.pathname, markModuleAsUsedByRoute]);

  /**
   * Auto-track on route change
   */
  useEffect(() => {
    if (!enabled) return;

    trackCurrentRoute();

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enabled, trackCurrentRoute]);

  return {
    markModuleAsUsed,
    markModuleAsUsedByRoute
  };
};

/**
 * Hook to track a specific module by name or route
 *
 * @param moduleIdentifier - Module name or route to track
 * @param trackImmediately - Whether to track immediately on mount (default: true)
 */
export const useTrackModule = (moduleIdentifier: string, trackImmediately: boolean = true) => {
  const { markModuleAsUsedByRoute } = useModuleUsageTracking(false);

  useEffect(() => {
    if (!trackImmediately) return;

    // Delay tracking slightly to ensure component has mounted
    const timer = setTimeout(() => {
      markModuleAsUsedByRoute(moduleIdentifier);
    }, 1000);

    return () => clearTimeout(timer);
  }, [moduleIdentifier, trackImmediately, markModuleAsUsedByRoute]);
};
